// Session keys the cache-buster never removes: auth, flash, location, and
// the last-modified stamp it compares on the next request.
const isProtectedSessionKey = (key) =>
  key === "rill_auth" ||
  key === "location" ||
  key === "_last_modified" ||
  (typeof key === "string" && key.startsWith("@rill/flash"));

/**
 * Deletes session cache entries the same way cache-buster does once it knows
 * these collections changed.
 *
 * A `no-bust` entry is removed only when its meta names one of `resources`.
 * With `dropUnpinned`, entries that are not marked `no-bust` are removed too
 * (cache-buster's default once a /modified document is present).
 *
 * Keys are collected before deletion so splicing `session.items` cannot skip
 * an entry. One-shot keys written by setSession (`editCycle`, `editExercises`)
 * are `no-bust` without a collection name, so they survive and the loader can
 * still return the payload the save just stored.
 */
export function bustUpdatedResources(session, resources, options = {}) {
  const { dropUnpinned = false } = options;
  if (!session?.items) return [];
  const updated = new Set(resources || []);
  const keys = [];
  for (const item of session.items) {
    const key = item?.key;
    if (!key || isProtectedSessionKey(key)) continue;
    const meta = item.meta || [];
    const pinned = meta.includes("no-bust");
    if (pinned) {
      if (meta.some((name) => updated.has(name))) keys.push(key);
      continue;
    }
    if (dropUnpinned) keys.push(key);
  }
  for (const key of keys) session.delete(key);
  return keys;
}

// Full clear, matching cache-buster's bustSession (protected keys stay).
export function bustAllSessionCache(session) {
  if (!session?.items) return [];
  const keys = [];
  for (const item of session.items) {
    const key = item?.key;
    if (!key || isProtectedSessionKey(key)) continue;
    keys.push(key);
  }
  for (const key of keys) session.delete(key);
  return keys;
}

// Collections behind the program editor and the Saved Workouts lists.
export const PROGRAM_EDITOR_RESOURCES = [
  "exercise",
  "exercise-section",
  "cycle",
  "workout-folder",
];

/**
 * Drop cached editExercises, editSections, editCycle, savedWorkouts, and
 * workoutFolders entries (and any other loader tagged with those collections)
 * after a successful save, so the next render does not wait on /modified.
 */
export function bustProgramEditorCache(session) {
  return bustUpdatedResources(session, PROGRAM_EDITOR_RESOURCES, {
    dropUnpinned: true,
  });
}
